import React from "react";

import * as Frigade from "@frigade/react";
import { UserWithBillingPlan } from "../components/context/AppContext";
import { useHistory } from "react-router-dom";

interface FrigadeProviderProps {
  children: JSX.Element;
  user?: UserWithBillingPlan;
  defaultCollection: boolean;
}

const FRIGADE_PROD_KEY =
  "api_public_tD9xgiVkQjHHAFKd7CP6bgZLnF4dy9foch3WznIe57ffo3NfRij62u9PK1welfzk";
const FRIGADE_DEV_KEY =
  "api_public_IVHC1WfQZ3fMWV7WhWAfST1tr3szePeu6fD6YYMb2zrek50vCLkOP07FpS1nkZ76";

export const npsFlowId = "flow_EGJ02ltd";
export const upgradeRequestFlowId = "flow_0xYknjPv";

// Calling Frigade hooks (e.g. addProperties) outside of a mounted Frigade.Provider throws
// inside the SDK ("reading 'config'" / "reading 'identify'"). Keep this gate in sync with
// the provider mount check below.
export const isFrigadeEnabled = (
  user: UserWithBillingPlan | undefined,
): user is UserWithBillingPlan => {
  if (!user) return false;
  // ddtest2@merge.dev and its organization (0d6cbf13-...) are the DataDog synthetics
  // monitoring account that hits the dashboard on a schedule. Excluded so the bot
  // doesn't pollute Frigade analytics or trigger onboarding flows.
  if (user.email === "ddtest2@merge.dev") return false;
  if (user.organization.id === "0d6cbf13-f96d-4ec7-939f-07805f15878b") return false;
  return true;
};

export const FrigadeProvider = ({ children, user, defaultCollection }: FrigadeProviderProps) => {
  const history = useHistory();

  const publicApiKey =
    process.env.REACT_APP_MERGE_ENV === "PRODUCTION" ? FRIGADE_PROD_KEY : FRIGADE_DEV_KEY;

  // Disable frigade for datadog synthetics tests
  if (!isFrigadeEnabled(user)) {
    return children;
  }

  return (
    <Frigade.Provider
      apiKey={publicApiKey}
      defaultCollection={defaultCollection}
      userId={user.email}
      groupId={user.organization.id}
      theme={{
        radii: {
          md: "6px",
        },
        colors: {
          neutral: {
            border: "#E2E2E2",
            foreground: "#000000",
            "400": "#777674",
          },
          primary: {
            foreground: "#fff",
            background: "#2C2A25",
            surface: "#2C2A25",
            border: "#2C2A25",
            hover: {
              background: "#565551",
              surface: "#565551",
              border: "#565551",
              foreground: "#fff",
            },
            active: {
              background: "#565551",
              surface: "#565551",
              border: "#565551",
              foreground: "#fff",
            },
            focus: {
              background: "#2C2A25",
              surface: "#2C2A25",
              border: "#2C2A25",
              foreground: "#fff",
            },
          },
          secondary: {
            foreground: "#111317",
          },
        },
      }}
      navigate={(url, target): void => {
        if (target === "_blank") {
          window.open(url, "_blank");
        } else {
          history.push(url);
        }
      }}
    >
      {children}
    </Frigade.Provider>
  );
};
